// components/steps/StepCompanions.jsx
import React, { useState, useEffect } from 'react';

const companionTypes = [
  'Solo',
  'Partner',
  'Friend Group',
  'Business Associate',
  'Other'
];

const StepCompanions = ({ data, updateData, skip, goBack }) => {
  // 0 = choose type, 1 = follow-up (kids or count)
  const [stage, setStage] = useState(0);

  // Collected fields
  const [type, setType] = useState('');
  const [hasKids, setHasKids] = useState(null);
  const [count, setCount] = useState('');

  // Restore saved data & decide initial stage
  useEffect(() => {
    if (!data.companions) return;
    const c = data.companions;
    // If it was a simple string (legacy), just pick type and stay at 0
    if (typeof c === 'string') {
      setType(c);
      return;
    }
    // Otherwise assume shape {type, hasKids?, count?}
    setType(c.type);
    if (c.type === 'Partner' || c.type === 'Friend Group' || c.type === 'Business Associate') {
      setStage(1);
      if (c.type === 'Partner') setHasKids(c.hasKids);
      setCount(c.count);
    } else {
      setStage(0);
    }
  }, [data.companions]);

  // Validation: can hit Next?
  const isValid = () => {
    if (stage === 0) return !!type;
    if (type === 'Partner') {
      if (hasKids === null) return false;
      return hasKids ? count > 0 : true;
    }
    // Friend Group or Business Associate
    return count > 0;
  };

  // Unified Next handler
  const handleNext = () => {
    if (!isValid()) return;

    if (stage === 0) {
      // Solo/Other: finish immediately
      if (type === 'Solo' || type === 'Other') {
        updateData('companions', { type });
        return;
      }
      // otherwise go to follow-up
      return setStage(1);
    }

    // Follow-up stage
    if (type === 'Partner') {
      if (!hasKids) {
        updateData('companions', { type, hasKids: false, count: 0 });
      } else {
        updateData('companions', { type, hasKids: true, count: Number(count) });
      }
    } else {
      // Friend Group or Business Associate
      updateData('companions', { type, count: Number(count) });
    }
  };

  return (
    <div className="bg-white/60 backdrop-blur p-6 rounded-xl shadow-lg max-w-md mx-auto">
      {stage === 0 && (
        <>
          <h2 className="text-xl font-semibold mb-4 text-center">
            Who are you traveling with?
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
            {companionTypes.map(opt => (
              <button
                key={opt}
                onClick={() => setType(opt)}
                className={`px-4 py-2 rounded-lg border text-sm transition-colors
                  ${type === opt
                    ? 'bg-blue-500 text-white border-transparent'
                    : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-100'}`}
              >
                {opt}
              </button>
            ))}
          </div>
        </>
      )}

      {stage === 1 && type === 'Partner' && (
        <>
          <h2 className="text-xl font-semibold mb-4 text-center">
            Are you traveling with children?
          </h2>
          <div className="flex gap-4 mb-6">
            {['Yes', 'No'].map(ans => (
              <button
                key={ans}
                onClick={() => setHasKids(ans === 'Yes')}
                className={`flex-1 px-4 py-2 rounded-lg border text-sm transition-colors
                  ${hasKids === (ans === 'Yes')
                    ? 'bg-blue-500 text-white border-transparent'
                    : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-100'}`}
              >
                {ans}
              </button>
            ))}
          </div>
          {hasKids && (
            <div className="mb-6">
              <h3 className="font-medium mb-2">How many children?</h3>
              <input
                type="number"
                min="1"
                className="w-full p-3 border rounded-lg"
                placeholder="Enter number of children"
                value={count}
                onChange={e => setCount(e.target.value)}
              />
            </div>
          )}
        </>
      )}

      {stage === 1 &&
        (type === 'Friend Group' || type === 'Business Associate') && (
          <>
            <h2 className="text-xl font-semibold mb-4 text-center">
              How many {type === 'Friend Group' ? 'friends' : 'colleagues'}?
            </h2>
            <input
              type="number"
              min="1"
              className="w-full p-3 border rounded-lg mb-6"
              placeholder="Enter a number"
              value={count}
              onChange={e => setCount(e.target.value)}
            />
          </>
        )}

      {/* Navigation */}
      <div className="flex justify-between items-center">
        <button
          onClick={() => (stage > 0 ? setStage(stage - 1) : goBack())}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
        >
          Back
        </button>

        <div className="flex items-center gap-2">
          <button
            onClick={handleNext}
            disabled={!isValid()}
            className={`px-6 py-2 rounded-lg transition 
              ${!isValid()
                ? 'bg-blue-300 text-white cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'}`}
          >
            Next
          </button>
          <button
            onClick={skip}
            className="px-6 py-2 bg-transparent border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition"
          >
            Skip
          </button>
        </div>
      </div>
    </div>
  );
};

export default StepCompanions;
